import { map } from "lodash";
import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import {
  DataGridPremium,
  useGridApiRef,
  GridToolbar,
  // GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  // useKeepGroupedColumnsHidden,
} from "@mui/x-data-grid-premium";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Chip as MuiChip,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Link,
  Paper as MuiPaper,
  Toolbar,
  Tooltip,
  Typography,
  Avatar,
  useTheme,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { green, yellow, blue, grey, red } from "@mui/material/colors";
import {
  Add as AddIcon,
  Archive as ArchiveIcon,
  FilterList as FilterListIcon,
  RemoveRedEye as RemoveRedEyeIcon,
} from "@mui/icons-material";
import { spacing } from "@mui/system";

import OpenOrder from "../../components/dialogs/WorkOrder";

import { fetchWorkordersAsync } from "../../redux/slices/jobs";
import { fetchCustomers } from "../../redux/slices/customers";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

const Chip = styled(MuiChip)`
  ${spacing};

  background: ${(props) =>
    props.background.toLowerCase() === "open" && green[500]};
  background: ${(props) =>
    props.background.toLowerCase() === "on hold" && yellow[700]};
  background: ${(props) =>
    props.background.toLowerCase() === "pulling" && blue[500]};
  background: ${(props) =>
    props.background.toLowerCase() === "cancelled" && red[500]};
  background: ${(props) =>
    props.background.toLowerCase() === "closed" && grey[500]};
  color: ${(props) => props.theme.palette.common.white};
`;

const Spacer = styled.div`
  flex: 1 1 100%;
`;

const ToolbarTitle = styled.div`
  min-width: 150px;
`;

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => ({
    el,
    index,
  }));
  stabilizedThis.sort((a, b) => {
    const order = comparator(a.el, b.el);
    if (order !== 0) return order;
    return a.index - b.index;
  });
  return stabilizedThis.map((element) => element.el);
}

const headCells = [
  { id: "id", alignment: "right", label: "WO", show: "xs" },
  { id: "customer", alignment: "left", label: "Customer", show: "xs" },
  { id: "description", alignment: "left", label: "Summary", show: "md" },
  { id: "date", alignment: "left", label: "Date", show: "md" },
  { id: "daily_rate", alignment: "right", label: "Day Rate", show: "md" },
  { id: "status", alignment: "left", label: "Status", show: "sm" },
  { id: "dt", alignment: "left", label: "Delivery Ticket", show: "sm" },
  { id: "salesman", alignment: "left", label: "Rep", show: "md" },
  { id: "actions", alignment: "right", label: "Actions", show: "xs" },
];

const WorkorderGrid = () => {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { workorders, idle } = useSelector((state) => state.jobs);
  const { customers } = useSelector((state) => state.customers);
  const rows = React.useMemo(
    () =>
      map(workorders, (workorder, id) => {
        return {
          ...workorder,
          id,
        };
      }),
    [workorders]
  );
  const columns = [
    {
      field: "wo",
      headerName: "WO",
      flex: 1,
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 1,
      renderCell: ({ value }) => {
        return customers?.[value]?.name;
      },
    },
  ];
  React.useEffect(() => {
    dispatch(fetchWorkordersAsync());
    dispatch(fetchCustomers());
  }, []);

  return (
    <Box sx={{ height: "calc(90vh - 95px)", width: "100%" }}>
      <DataGridPremium
        rows={rows}
        columns={columns}
        density="compact"
        // onRowDoubleClick={handleReportEdit}
        apiRef={apiRef}
        loading={idle}
        disableRowSelectionOnClick
        components={{
          Toolbar: GridToolbar,
        }}
      />
    </Box>
  );
};

function OrderList() {
  const defaultOrderState = { open: false, orderId: null };
  const [openOrder, setOpenOrder] = React.useState(defaultOrderState);
  const closeOrder = () => setOpenOrder(defaultOrderState);
  const toggleOpenOrder = (id) =>
    setOpenOrder({ open: true, orderId: id || null });

  return (
    <React.Fragment>
      <Helmet title="Orders" />

      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Work Orders
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/">
              Pages
            </Link>
            <Typography>Orders</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={toggleOpenOrder}
            >
              <AddIcon />
              New Order
            </Button>
          </div>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <WorkorderGrid />
        </Grid>
      </Grid>

      <OpenOrder open={openOrder.open} onClose={closeOrder} />
    </React.Fragment>
  );
}

export default OrderList;
